import { Component, ErrorInfo, ReactNode } from "react";
import { Button } from "@/components/ui";
import { AlertTriangle, RefreshCw } from "lucide-react";
import { esErrorDeChunk, recargarPorVersionNueva } from "@/lib/lazyConReintento";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("Error caught by boundary:", error, errorInfo);
    // Un chunk que ya no existe no es un error de la app: hay una versión
    // nueva. Se recarga una vez en silencio; si vuelve a fallar, se muestra.
    if (esErrorDeChunk(error) && recargarPorVersionNueva()) return;
    this.setState({
      error,
      errorInfo,
    });
  }

  handleReset = () => {
    this.setState({ hasError: false, error: undefined, errorInfo: undefined });
  };

  render() {
    if (this.state.hasError) {
      const versionNueva = esErrorDeChunk(this.state.error);
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
          <div className="max-w-md w-full bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
            <div className="text-center">
              <AlertTriangle className="mx-auto h-12 w-12 text-red-500 mb-4" />
              <h1 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                {versionNueva ? "Hay una versión nueva" : "Algo salió mal"}
              </h1>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                {versionNueva
                  ? "La aplicación se actualizó mientras la tenías abierta. Recargá la página para seguir."
                  : "Ha ocurrido un error inesperado. Por favor, intenta recargar la página."}
              </p>

              <div className="space-y-3">
                <Button
                  onClick={this.handleReset}
                  className="w-full"
                  variant="outline"
                >
                  <RefreshCw className="mr-2 h-4 w-4" />
                  Intentar de nuevo
                </Button>

                <Button
                  onClick={() => window.location.reload()}
                  className="w-full"
                >
                  Recargar página
                </Button>
              </div>

              {import.meta.env.DEV && this.state.error && (
                <details className="mt-6 text-left">
                  <summary className="cursor-pointer text-sm text-gray-500 dark:text-gray-400 mb-2">
                    Detalles del error (solo desarrollo)
                  </summary>
                  <div className="bg-gray-100 dark:bg-gray-700 p-3 rounded text-xs font-mono text-gray-800 dark:text-gray-200 overflow-auto">
                    <div className="mb-2">
                      <strong>Error:</strong> {this.state.error.toString()}
                    </div>
                    {this.state.errorInfo && (
                      <div>
                        <strong>Stack:</strong>
                        <pre className="whitespace-pre-wrap mt-1">
                          {this.state.errorInfo.componentStack}
                        </pre>
                      </div>
                    )}
                  </div>
                </details>
              )}
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
